import {
  UsersApi,
  MapUserResponse,
  RegisterUserRequest,
  UpdateProfileRequest,
  UpdateProfileResponse,
  AccountDeletionImpactResponse,
  DeleteAccountRequest,
  DeleteAccountResponse
} from 'we-tree-api-client/v2';
import { fetchUserAttributes } from 'aws-amplify/auth';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export type { UpdateProfileRequest, UpdateProfileResponse };
export type { AccountDeletionImpactResponse, DeleteAccountRequest, DeleteAccountResponse };

/**
 * Wrapped users API with standardized error handling
 */
class WrappedUsersApi {
  /**
   * Register a new user
   */
  async registerUser(): Promise<ApiResult<MapUserResponse>> {
    try {
      // Get user attributes from Cognito
      const userAttributes = await fetchUserAttributes();
      const email = userAttributes.email || '';
      const nickname = userAttributes.nickname || email.split('@')[0] || 'User';
      const cognitoId = userAttributes.sub || '';

      if (!email || !cognitoId) {
        return {
          success: false,
          error: new (await import('./errors')).ConfigurationError('Missing required user attributes from Cognito')
        };
      }

      const registerUserRequest: RegisterUserRequest = {
        email,
        nickname,
        id: cognitoId
      };

      return withApiSetup(
        (config) => new UsersApi(config),
        getApiConfiguration,
        (api) => api.registerUser(registerUserRequest)
      );
    } catch (error) {
      return {
        success: false,
        error: new (await import('./errors')).ConfigurationError(
          `Failed to get user attributes: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      };
    }
  }

  /**
   * Update the authenticated user's profile (email and/or nickname)
   * Syncs changes to we-tree database after Cognito attribute updates
   */
  async updateProfile(request: UpdateProfileRequest): Promise<ApiResult<UpdateProfileResponse>> {
    return withApiSetup(
      (config) => new UsersApi(config),
      getApiConfiguration,
      (api) => api.updateProfile(request)
    );
  }

  /**
   * Preview impact of deleting the authenticated account
   */
  async getAccountDeletionImpact(): Promise<ApiResult<AccountDeletionImpactResponse>> {
    return withApiSetup(
      (config) => new UsersApi(config),
      getApiConfiguration,
      (api) => api.getAccountDeletionImpact()
    );
  }

  /**
   * Delete the authenticated user's account
   */
  async deleteAccount(request: DeleteAccountRequest): Promise<ApiResult<DeleteAccountResponse>> {
    return withApiSetup(
      (config) => new UsersApi(config),
      getApiConfiguration,
      (api) => api.deleteAccount(request)
    );
  }
}

/**
 * Wrapped users API instance with standardized error handling
 */
export const usersApi = new WrappedUsersApi();

export default usersApi;
